import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
// Главная — лендинг по ТЗ от августа 2026, см. landing/v4.
// Прежняя версия осталась в landing/v2 и в маршрутах не участвует.
import { MainV4 as Main } from '@landing/v4/MainV4';
import { Child } from '@landing/pages/Child';
import { User } from '@landing/pages/User';
import { Cookie } from '@landing/pages/Cookie';
import { Privacy } from '@landing/pages/Privacy';
import { Personal } from '@landing/pages/Personal';
import { RefCode } from '@landing/pages/RefCode';
import { PublicRedirect } from '@app/components/PublicRedirect';
import { ProtectedRoute, RegistrationRoute } from '@app/layouts/ProtectedRoute';

const AppLayout = lazy(() =>
  import('@app/layouts/AppLayout').then((m) => ({ default: m.AppLayout }))
);
const AuthLayout = lazy(() =>
  import('@app/layouts/AuthLayout').then((m) => ({ default: m.AuthLayout }))
);
const ProfileScreen = lazy(() =>
  import('@app/features/profile/ProfileScreen').then((m) => ({
    default: m.ProfileScreen,
  }))
);
const SettingsScreen = lazy(() =>
  import('@app/features/settings/SettingsScreen').then((m) => ({
    default: m.SettingsScreen,
  }))
);
const AndroidPage = lazy(() =>
  import('@landing/v4/android/AndroidV4').then((m) => ({ default: m.AndroidV4 }))
);
const RegistrationScreen = lazy(() =>
  import('@app/features/registration/RegistrationScreen').then((m) => ({
    default: m.RegistrationScreen,
  }))
);
const KycScreen = lazy(() =>
  import('@app/features/kyc/KycScreen').then((m) => ({ default: m.KycScreen }))
);
const FeedScreen = lazy(() =>
  import('@app/features/feed/FeedScreen').then((m) => ({
    default: m.FeedScreen,
  }))
);
const ReelsScreen = lazy(() =>
  import('@app/features/reels/ReelsScreen').then((m) => ({ default: m.ReelsScreen }))
);
const CommunitiesListScreen = lazy(() =>
  import('@app/features/communities/CommunitiesListScreen').then((m) => ({
    default: m.CommunitiesListScreen,
  }))
);
const CommunityDetailScreen = lazy(() =>
  import('@app/features/communities/CommunityDetailScreen').then((m) => ({
    default: m.CommunityDetailScreen,
  }))
);
const WalletScreen = lazy(() =>
  import('@app/features/wallet/WalletParityScreen').then((m) => ({
    default: m.WalletParityScreen,
  }))
);
const SubscriptionsScreen = lazy(() =>
  import('@app/features/wallet/SubscriptionsScreen').then((m) => ({
    default: m.SubscriptionsScreen,
  }))
);
const ShopDraftsScreen = lazy(() =>
  import('@app/features/shop/MyListingsScreen').then((m) => ({
    default: m.ShopDraftsScreen,
  }))
);
const StoreCategoriesScreen = lazy(() =>
  import('@app/features/shop/StoreCategoriesScreen').then((m) => ({
    default: m.StoreCategoriesScreen,
  }))
);
const ShopScreen = lazy(() =>
  import('@app/features/shop/ShopScreen').then((m) => ({ default: m.ShopScreen }))
);
const ListingDetailScreen = lazy(() =>
  import('@app/features/shop/ListingDetailScreen').then((m) => ({
    default: m.ListingDetailScreen,
  }))
);
const CreateListingScreen = lazy(() =>
  import('@app/features/shop/create/CreateListingScreen').then((m) => ({
    default: m.CreateListingScreen,
  }))
);
const SellerProfileScreen = lazy(() =>
  import('@app/features/shop/SellerProfileScreen').then((m) => ({
    default: m.SellerProfileScreen,
  }))
);
const MyListingsScreen = lazy(() =>
  import('@app/features/shop/MyListingsScreen').then((m) => ({
    default: m.MyListingsScreen,
  }))
);
const DealsScreen = lazy(() =>
  import('@app/features/shop/DealsScreen').then((m) => ({ default: m.DealsScreen }))
);
const DealDetailScreen = lazy(() =>
  import('@app/features/shop/DealDetailScreen').then((m) => ({
    default: m.DealDetailScreen,
  }))
);
const SpecialistsScreen = lazy(() =>
  import('@app/features/pros/Catalog').then((m) => ({
    default: m.ProsCatalog,
  }))
);
const SpecialistDetailScreen = lazy(() =>
  import('@app/features/pros/ListingScreens').then((m) => ({
    default: m.ProListingScreen,
  }))
);
const MySpecialistProfileScreen = lazy(() =>
  import('@app/features/pros/ListingScreens').then((m) => ({
    default: m.MyProListings,
  }))
);
const BookingsScreen = lazy(() =>
  import('@app/features/pros/BookingScreens').then((m) => ({
    default: m.ProBookings,
  }))
);
const ListingWizard = lazy(() =>
  import('@app/features/pros/ListingWizard').then((m) => ({ default: m.ListingWizard }))
);
const ProDrafts = lazy(() =>
  import('@app/features/pros/ListingScreens').then((m) => ({ default: m.ProDrafts }))
);
const PublicProProfile = lazy(() =>
  import('@app/features/pros/ListingScreens').then((m) => ({
    default: m.PublicProProfile,
  }))
);
const ProSchedule = lazy(() =>
  import('@app/features/pros/BookingScreens').then((m) => ({ default: m.ProSchedule }))
);
const ProCalendar = lazy(() =>
  import('@app/features/pros/BookingScreens').then((m) => ({ default: m.ProCalendar }))
);
const GiftsScreen = lazy(() =>
  import('@app/features/gifts/GiftsScreen').then((m) => ({ default: m.GiftsScreen }))
);
const PostDetailScreen = lazy(() =>
  import('@app/features/post-detail/PostDetailScreen').then((m) => ({
    default: m.PostDetailScreen,
  }))
);
const NewsScreen = lazy(() =>
  import('@app/features/news/NewsScreen').then((m) => ({ default: m.NewsScreen }))
);
const NewsDetailScreen = lazy(() =>
  import('@app/features/news/NewsDetailScreen').then((m) => ({
    default: m.NewsDetailScreen,
  }))
);
const PartnersScreen = lazy(() =>
  import('@app/features/partners/PartnersScreen').then((m) => ({
    default: m.PartnersScreen,
  }))
);
const PartnerDetailScreen = lazy(() =>
  import('@app/features/partners/PartnerDetailScreen').then((m) => ({
    default: m.PartnerDetailScreen,
  }))
);
const NotificationsScreen = lazy(() =>
  import('@app/features/notifications/NotificationsScreen').then((m) => ({
    default: m.NotificationsScreen,
  }))
);
const SearchScreen = lazy(() =>
  import('@app/features/search/SearchScreen').then((m) => ({
    default: m.SearchScreen,
  }))
);
const FollowListScreen = lazy(() =>
  import('@app/features/follow-list/FollowListScreen').then((m) => ({
    default: m.FollowListScreen,
  }))
);
const DialogsListScreen = lazy(() =>
  import('@app/features/messenger/MessengerList').then((m) => ({
    default: m.MessengerList,
  }))
);
const ConversationScreen = lazy(() =>
  import('@app/features/messenger/MessengerThread').then((m) => ({
    default: m.MessengerThread,
  }))
);
const HubScreen = lazy(() =>
  import('@app/features/hub/HubScreen').then((m) => ({ default: m.HubScreen }))
);
const HubCatalogPage = lazy(() =>
  import('@app/features/hub/HubScreen').then((m) => ({ default: m.HubCatalogPage }))
);
const HubSettingsScreen = lazy(() =>
  import('@app/features/hub/HubScreen').then((m) => ({ default: m.HubSettingsScreen }))
);
const FavoritesScreen = lazy(() =>
  import('@app/features/hub/FavoritesScreen').then((m) => ({ default: m.HubFavorites }))
);
const CallHistoryScreen = lazy(() =>
  import('@app/features/calls/CallHistoryScreen').then((m) => ({
    default: m.CallHistoryScreen,
  }))
);
const CallRouteScreen = lazy(() =>
  import('@app/features/calls/CallRouteScreen').then((m) => ({
    default: m.CallRouteScreen,
  }))
);
const EventsListScreen = lazy(() =>
  import('@app/features/events/EventsListScreen').then((m) => ({
    default: m.EventsListScreen,
  }))
);
const EventDetailScreen = lazy(() =>
  import('@app/features/events/EventDetailScreen').then((m) => ({
    default: m.EventDetailScreen,
  }))
);
const MapsScreen = lazy(() =>
  import('@app/features/maps/MapsScreen').then((m) => ({
    default: m.MapsScreen,
  }))
);
const RatingScreen = lazy(() =>
  import('@app/features/rating/RatingScreen').then((m) => ({
    default: m.RatingScreen,
  }))
);
const DatingQuestionnaireScreen = lazy(() =>
  import('@app/features/dating-v1/ProfileEditor').then((m) => ({
    default: m.DatingProfileEditor,
  }))
);
const MyDatingProfileScreen = DatingQuestionnaireScreen;
const SympathiesScreen = lazy(() =>
  import('@app/features/dating-v1/DatingScreen').then((m) => ({
    default: () => <m.DatingScreen initialTab="incoming" />,
  }))
);
const DatingScreen = lazy(() =>
  import('@app/features/dating-v1/DatingScreen').then((m) => ({
    default: m.DatingScreen,
  }))
);
function AppFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-body)',
      }}
    >
      Загрузка Aura…
    </div>
  );
}

const withSuspense = (el: React.ReactNode) => (
  <Suspense fallback={<AppFallback />}>{el}</Suspense>
);

export const router = createBrowserRouter([
  // ── Landing ───────────────────────────────────────────────────────
  { path: '/', Component: Main },
  ...(['profile', 'post', 'shop', 'specialists'] as const).map((kind) => ({
    path: `${kind}/:id`,
    element: <PublicRedirect kind={kind} />,
  })),
  { path: 'child_protection', Component: Child },
  { path: 'user_agreement', Component: User },
  { path: 'cookie_policy', Component: Cookie },
  { path: 'privacy_policy', Component: Privacy },
  { path: 'personal_data', Component: Personal },
  { path: 'ref', Component: RefCode },
  // iOS делится приглашением как /auth/signup?ref=<код> — та же страница.
  { path: 'auth/signup', Component: RefCode },

  // Инструкция по установке на Android — отдельная страница дистрибуции.
  { path: 'android', element: withSuspense(<AndroidPage />) },

  // ── Aura Web App ──────────────────────────────────────────────────
  {
    path: 'app/auth',
    element: withSuspense(<AuthLayout />),
  },
  {
    path: 'app/registration',
    element: (
      <RegistrationRoute>{withSuspense(<RegistrationScreen />)}</RegistrationRoute>
    ),
  },
  {
    path: 'app',
    element: <ProtectedRoute>{withSuspense(<AppLayout />)}</ProtectedRoute>,
    children: [
      { index: true, element: <Navigate to="/app/feed" replace /> },
      {
        path: 'feed',
        element: withSuspense(<FeedScreen />),
      },
      {
        path: 'reels',
        element: withSuspense(<ReelsScreen />),
      },
      {
        path: 'communities',
        element: withSuspense(<CommunitiesListScreen />),
      },
      {
        path: 'communities/:id',
        element: withSuspense(<CommunityDetailScreen />),
      },
      {
        path: 'people',
        element: withSuspense(<DatingScreen />),
      },
      {
        path: 'dating/questionnaire',
        element: withSuspense(<DatingQuestionnaireScreen />),
      },
      {
        path: 'dating/profile',
        element: withSuspense(<MyDatingProfileScreen />),
      },
      {
        path: 'dating/sympathies',
        element: withSuspense(<SympathiesScreen />),
      },
      {
        path: 'dialogs',
        element: withSuspense(<DialogsListScreen />),
      },
      {
        path: 'dialogs/:chatId',
        element: withSuspense(<ConversationScreen />),
      },
      { path: 'hub', element: withSuspense(<HubScreen />) },
      { path: 'hub/settings', element: withSuspense(<HubSettingsScreen />) },
      { path: 'hub/favorites', element: withSuspense(<FavoritesScreen />) },
      { path: 'hub/:page', element: withSuspense(<HubCatalogPage />) },
      { path: 'calls', element: withSuspense(<CallHistoryScreen />) },
      {
        path: 'call/:roomId',
        element: withSuspense(<CallRouteScreen />),
      },
      {
        path: 'events',
        element: withSuspense(<EventsListScreen />),
      },
      {
        path: 'events/:id',
        element: withSuspense(<EventDetailScreen />),
      },
      {
        path: 'nearby',
        element: withSuspense(<MapsScreen />),
      },
      {
        path: 'rating',
        element: withSuspense(<RatingScreen />),
      },
      {
        path: 'search',
        element: withSuspense(<SearchScreen />),
      },
      {
        path: 'notifications',
        element: withSuspense(<NotificationsScreen />),
      },
      { path: 'news', element: withSuspense(<NewsScreen />) },
      { path: 'news/:id', element: withSuspense(<NewsDetailScreen />) },
      { path: 'partners', element: withSuspense(<PartnersScreen />) },
      { path: 'partners/:id', element: withSuspense(<PartnerDetailScreen />) },
      { path: 'profile', element: withSuspense(<ProfileScreen />) },
      { path: 'profile/:id', element: withSuspense(<ProfileScreen />) },
      { path: 'profile/:id/:tab', element: withSuspense(<FollowListScreen />) },
      // ── Монетизация и маркетплейсы ────────────────────────────────
      { path: 'wallet', element: withSuspense(<WalletScreen />) },
      { path: 'wallet/return', element: withSuspense(<WalletScreen />) },
      { path: 'subscriptions', element: withSuspense(<SubscriptionsScreen />) },
      { path: 'gifts', element: withSuspense(<GiftsScreen />) },
      { path: 'shop', element: withSuspense(<ShopScreen />) },
      { path: 'shop/drafts', element: withSuspense(<ShopDraftsScreen />) },
      { path: 'shop/categories', element: withSuspense(<StoreCategoriesScreen />) },
      { path: 'shop/new', element: withSuspense(<CreateListingScreen />) },
      { path: 'shop/my', element: withSuspense(<MyListingsScreen />) },
      {
        path: 'shop/seller/:userId',
        element: withSuspense(<SellerProfileScreen />),
      },
      { path: 'shop/:id/edit', element: withSuspense(<CreateListingScreen />) },
      { path: 'shop/deals', element: withSuspense(<DealsScreen />) },
      { path: 'shop/deals/:id', element: withSuspense(<DealDetailScreen />) },
      { path: 'shop/:id', element: withSuspense(<ListingDetailScreen />) },
      { path: 'specialists', element: withSuspense(<SpecialistsScreen />) },
      {
        path: 'specialists/bookings',
        element: withSuspense(<BookingsScreen />),
      },
      {
        path: 'specialists/me',
        element: withSuspense(<MySpecialistProfileScreen />),
      },
      { path: 'specialists/:id', element: withSuspense(<SpecialistDetailScreen />) },
      {
        path: 'specialists/listings/:id',
        element: withSuspense(<SpecialistDetailScreen />),
      },
      { path: 'specialists/listings/new', element: withSuspense(<ListingWizard />) },
      {
        path: 'specialists/listings/:id/edit',
        element: withSuspense(<ListingWizard />),
      },
      { path: 'specialists/drafts', element: withSuspense(<ProDrafts />) },
      {
        path: 'specialists/profile/:userId',
        element: withSuspense(<PublicProProfile />),
      },
      { path: 'specialists/schedule', element: withSuspense(<ProSchedule />) },
      { path: 'specialists/calendar', element: withSuspense(<ProCalendar />) },

      { path: 'settings', element: withSuspense(<SettingsScreen />) },
      { path: 'kyc', element: withSuspense(<KycScreen />) },
      { path: 'post/:id', element: withSuspense(<PostDetailScreen />) },
    ],
  },
]);
